<template>
  <section
    id="rating"
    data-qa="lpu_rating"
    class="lpu-rating bg-gray-40 rounded-30"
    :class="{
      'lpu-rating--mobile pa-4': isMobile,
      'pa-8': !isMobile,
      'lpu-rating--expandable': canExpand,
    }"
    :role="canExpand ? 'button' : undefined"
    :tabindex="canExpand ? 0 : undefined"
    :aria-expanded="canExpand ? (isExpanded ? 'true' : 'false') : undefined"
    @click="onBlockClick"
    @keydown.enter.prevent="onBlockClick"
    @keydown.space.prevent="onBlockClick"
  >
    <div class="d-flex flex-column">
      <div
        class="d-flex flex-column"
        :class="isMobile ? 'ga-5' : 'ga-3'"
      >
        <div
          class="d-flex align-center"
          :class="isMobile ? 'ga-3' : 'lpu-rating__title-row'"
        >
          <h2
            data-qa="lpu_rating_title"
            class="text--text"
            :class="isMobile ? 'text-h6' : 'text-h5'"
          >
            {{ tStatic('Рейтинг') }}
          </h2>

          <div
            v-if="canExpand"
            data-qa="lpu_rating_toggle"
            class="lpu-rating__toggle flex-shrink-0"
            aria-hidden="true"
          >
            <span
              class="lpu-rating__toggle-icon ui-icon-arrow-down ui-icon-fz-big"
              :class="[
                { 'lpu-rating__toggle-icon--expanded': isExpanded },
                ff_lpu_branding_page_v5
                  ? 'text-lpu-branding-color-text'
                  : 'text-lpu-branding-color-primary',
              ]"
            />
          </div>
        </div>

        <div
          :class="isMobile
            ? 'd-flex flex-column ga-6'
            : 'd-flex align-end ga-6'"
        >
          <div
            class="flex-shrink-0"
            :class="isMobile ? 'd-flex align-start ga-3' : 'd-flex flex-column ga-3'"
          >
            <div
              data-qa="lpu_rating_total_score"
              class="text--text font-weight-bold"
              :class="isMobile
                ? 'lpu-rating__total-score--mobile'
                : 'lpu-rating__total-score--desktop'"
            >
              {{ totalScoreText }}
            </div>
            <div class="lpu-rating__stars-card bg-gray-0 rounded-10 d-inline-flex align-center px-4 py-2">
              <StarsRate
                data-qa="lpu_rating_stars"
                :solidity="lpu.stars ?? 0"
                :color="hasFraudMark ? 'grey' : undefined"
              />
            </div>
          </div>

          <div
            class="flex-grow-1 min-w-0"
            :class="isMobile
              ? 'd-flex flex-column ga-4'
              : 'lpu-rating__categories'"
          >
            <RatingCategoryCard
              class="flex-grow-1"
              :title="tStatic('Клиника')"
              :score="clinicCategory.score"
              :has-score="clinicCategory.hasScore"
              :icon-src="CLINIC_ICON_SRC"
            />
            <RatingCategoryCard
              v-if="showDoctorsRating"
              class="flex-grow-1"
              :title="tStatic('Врачи')"
              :score="doctorsCategory.score"
              :has-score="doctorsCategory.hasScore"
              :icon-src="DOCTORS_ICON_SRC"
            />
            <div
              v-else-if="!isMobile"
              class="flex-grow-1"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>

      <div
        v-if="hasFraudMark"
        data-qa="lpu_rating_fraud_mark"
        class="d-flex align-center"
        :class="isMobile ? 'mt-5' : 'mt-6'"
      >
        <div class="ui-icon-circle-info text-error--text mr-2 flex-shrink-0" />
        <div class="text-body-2 text-error--text">
          {{ fraudMarkText }}
        </div>
      </div>

      <div
        v-if="canExpand"
        data-qa="lpu_rating_details"
        class="lpu-rating__details-expand"
        :class="{ 'lpu-rating__details-expand--open': isExpanded }"
        :aria-hidden="isExpanded ? 'false' : 'true'"
        @click.stop
      >
        <div class="lpu-rating__details-expand-inner">
          <div
            :class="isMobile
              ? 'd-flex flex-column ga-4 pt-5'
              : 'lpu-rating__details--desktop pt-6'"
          >
            <template v-if="isMobile">
              <RatingStatItem
                v-for="stat in mobileStats"
                :key="stat.key"
                :title="stat.title"
                :value="stat.value"
                :is-large-scale="stat.isLargeScale"
                :has-fraud-mark="hasFraudMark"
              />
            </template>
            <template v-else>
              <div class="d-flex flex-column ga-5">
                <RatingStatItem
                  v-for="stat in visibleClinicStats"
                  :key="stat.key"
                  :title="stat.title"
                  :value="stat.value"
                  :is-large-scale="stat.isLargeScale"
                  :has-fraud-mark="hasFraudMark"
                />
              </div>
              <div
                v-if="showDoctorsRating"
                class="d-flex flex-column ga-5"
              >
                <RatingStatItem
                  v-for="stat in visibleDoctorsStats"
                  :key="stat.key"
                  :title="stat.title"
                  :value="stat.value"
                  :is-large-scale="stat.isLargeScale"
                  :has-fraud-mark="hasFraudMark"
                />
              </div>
              <div
                v-else
                aria-hidden="true"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import useContextStore from 'src/store/useContextStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import StarsRate from 'src/components/common/StarsRate/StarsRate.vue'
import {
  CLINIC_ICON_SRC,
  DOCTORS_ICON_SRC,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/constants'
import formatRatingScore from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/formatRatingScore'
import getLpuRatingStats from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/getLpuRatingStats'
import shouldHideDoctorsRating from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/shouldHideDoctorsRating'
import zipRatingStats from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/functions/zipRatingStats'
import RatingCategoryCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/components/RatingCategoryCard.vue'
import RatingStatItem from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuRating/components/RatingStatItem.vue'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import { ALERT_TEXT_DATA } from 'www/vue_components/common/MarkOfShame/constants'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()

const ff_lpu_branding_page_v5 = computed(() => (
  Boolean(context.request.feature_flags.ff_lpu_branding_page_v5)
))

const isStaffUser = computed(() => Boolean(context.request.user.is_staff))
const hasSomeRatesRating = computed(() => Boolean(context.has_some_rates_rating))
const changeCalcRatingOnLaboratory = computed(() => (
  Boolean(context.request.feature_flags.change_calc_rating_on_laboratory)
))
const hideDoctorsRating = computed(() => shouldHideDoctorsRating(
  changeCalcRatingOnLaboratory.value,
  props.lpu.lputype.id,
  isStaffUser.value,
))
const showDoctorsRating = computed(() => (
  !hideDoctorsRating.value
  && (Boolean(context.has_some_doctors_rating) || isStaffUser.value)
))
const hasFraudMark = computed(() => Boolean(props.lpu.has_fraud_mark))
const canExpand = computed(() => (
  hasSomeRatesRating.value
  || showDoctorsRating.value
  || isStaffUser.value
))

const isExpanded = ref(false)

const totalScoreText = computed(() => formatRatingScore(props.lpu.stars ?? 0))
const fraudMarkText = computed(() => tStatic(ALERT_TEXT_DATA.rating))

const clinicCategory = computed(() => {
  const hasScore = hasSomeRatesRating.value || isStaffUser.value

  return {
    hasScore,
    score: hasScore
      ? (props.lpu.lpustat?.rates_rating ?? 0)
      : null,
  }
})

const doctorsCategory = computed(() => {
  const hasScore = showDoctorsRating.value

  return {
    hasScore,
    score: hasScore
      ? (props.lpu.lpustat?.doctors_rating ?? 0)
      : null,
  }
})

const ratingStats = computed(() => getLpuRatingStats(props.lpu))
const visibleClinicStats = computed(() => (
  hasSomeRatesRating.value || isStaffUser.value
    ? ratingStats.value.clinicStats
    : []
))
const visibleDoctorsStats = computed(() => (
  showDoctorsRating.value
    ? ratingStats.value.doctorsStats
    : []
))
const mobileStats = computed(() => zipRatingStats(
  visibleClinicStats.value,
  visibleDoctorsStats.value,
))

function onBlockClick() {
  if (!canExpand.value) {
    return
  }

  isExpanded.value = !isExpanded.value
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.lpu-rating {
  &--expandable {
    cursor: pointer;
  }

  &__title-row {
    position: relative;
  }

  &__toggle {
    position: relative;
    width: 32px;
    height: 32px;
    border-radius: 18px;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background-color: var(--lpu-branding-color-primary, $ui-kit-primary);
      opacity: 0.15;
    }
  }

  &__toggle-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

    &--expanded {
      transform: translate(-50%, -50%) rotate(-180deg);
    }
  }

  &__stars-card {
    box-shadow:
      0 2px 24px 0 rgba(9, 42, 72, 0.08),
      0 0 2px 0 rgba(21, 40, 92, 0.06),
      0 1px 2px 0 rgba(5, 23, 40, 0.08);
  }

  &__total-score--desktop {
    font-size: 46px;
    line-height: normal;
  }

  &__total-score--mobile {
    font-size: 36px;
    line-height: normal;
  }

  &__details-expand {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    cursor: default;

    &--open {
      grid-template-rows: 1fr;
    }
  }

  &__details-expand-inner {
    overflow: hidden;
    min-height: 0;
  }

  &__categories {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__details--desktop {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 39px;
  }

  &--mobile &__stars-card {
    height: 40px;
    padding-top: 0;
    padding-bottom: 0;
  }
}

// На десктопе кнопка стоит справа от заголовка с фиксированным отступом слева
.lpu-rating:not(.lpu-rating--mobile) {
  .lpu-rating__toggle {
    position: absolute;
    left: 112px;
    top: 0;
  }
}
</style>
